import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Calendar, X, ChevronLeft, ChevronRight, Filter, ZoomIn, Grid3X3, SlidersHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import ImageLightbox from "@/components/ImageLightbox";
import LazyImage from "@/components/LazyImage";
import { useLanguage } from "@/i18n/LanguageContext";
import useCountUp from "@/hooks/useCountUp";

// Static projects data - no backend required
const STATIC_PROJECTS = [
  {
    id: 1,
    slug: "renovation-appartement-bruxelles",
    title: "Rénovation Appartement Bruxelles",
    description: "Transformation complète d'un appartement de 120m² avec cuisine ouverte et finitions haut de gamme.",
    location: "Bruxelles",
    category: "Rénovation Complète",
    year: "2024",
    before_image: "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800",
    after_image: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800"
  },
  {
    id: 2,
    slug: "salle-de-bain-waterloo",
    title: "Salle de Bain Moderne",
    description: "Création d'une salle de bain contemporaine avec douche à l'italienne et carrelage design.",
    location: "Waterloo",
    category: "Salle de Bain",
    year: "2024",
    before_image: "https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=800",
    after_image: "https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=800"
  },
  {
    id: 3,
    slug: "carrelage-uccle",
    title: "Carrelage Grand Format",
    description: "Pose de carrelage 80x80 dans salon et cuisine ouverte, finitions soignées.",
    location: "Uccle",
    category: "Carrelage",
    year: "2024",
    before_image: "https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=800",
    after_image: "https://images.unsplash.com/photo-1600566752355-35792bedcfea?w=800"
  },
  {
    id: 4,
    slug: "peinture-anderlecht",
    title: "Peinture & Décoration Intérieure",
    description: "Rafraîchissement complet avec peinture décorative et finitions soignées.",
    location: "Anderlecht",
    category: "Peinture",
    year: "2024",
    before_image: "https://images.unsplash.com/photo-1560185007-c5ca9d2c014d?w=800",
    after_image: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800"
  },
  {
    id: 5,
    slug: "plafonnage-lasne",
    title: "Plafonnage Villa",
    description: "Travaux de plafonnage et enduits décoratifs pour une villa de standing.",
    location: "Lasne",
    category: "Plafonnage",
    year: "2024",
    before_image: "https://images.unsplash.com/photo-1560185127-6a8c0c6c4e0a?w=800",
    after_image: "https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?w=800"
  },
  {
    id: 6,
    slug: "parquet-woluwe",
    title: "Pose de Parquet Massif",
    description: "Installation de parquet chêne massif dans une maison de maître.",
    location: "Woluwe-Saint-Pierre",
    category: "Pose de Sol",
    year: "2024",
    before_image: "https://images.unsplash.com/photo-1560448075-bb485b1d4e36?w=800",
    after_image: "https://images.unsplash.com/photo-1600607687644-c7171b42498f?w=800"
  }
];

// Animated Counter Component
const AnimatedCounter = ({ end, suffix = "", label }) => {
  const { count, elementRef } = useCountUp(end, 2000, true);
  
  return (
    <div ref={elementRef} className="text-center">
      <div className="text-4xl md:text-5xl font-['Cormorant_Garamond'] font-bold text-white counter-number">
        {count}{suffix}
      </div>
      <div className="text-white/80 text-sm mt-1">{label}</div>
    </div>
  );
};

const ProjectsPage = () => {
  const { t } = useLanguage();
  const [projects] = useState(STATIC_PROJECTS);
  const [filter, setFilter] = useState("all");
  const [selectedProject, setSelectedProject] = useState(null);
  const [viewMode, setViewMode] = useState("grid");
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const categories = [
    "all",
    ...new Set(projects.map((p) => p.category)),
  ];

  const filteredProjects =
    filter === "all"
      ? projects
      : projects.filter((p) => p.category === filter);

  const featuredImages = [
    "https://images.unsplash.com/photo-1758448500596-ce0e0239f1be?w=800",
    "https://images.unsplash.com/photo-1760072513367-55182245e76c?w=800",
    "https://images.unsplash.com/photo-1771862956369-c840b0a1fc9d?w=800",
    "https://images.unsplash.com/photo-1771239048293-72abf673adb2?w=800",
    "https://images.unsplash.com/photo-1758448500631-644bb3c1c942?w=800",
    "https://images.unsplash.com/photo-1769970502055-6d1de307406d?w=800",
  ];

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  return (
    <div data-testid="projects-page" className="pt-24 bg-[#0A0A0A]">
      {/* Hero Section - Dark */}
      <section className="relative bg-[#0A0A0A] py-24 lg:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <div className="grid grid-cols-3 gap-2 opacity-20 h-full">
            {featuredImages.slice(0, 6).map((img, i) => (
              <div key={i} className="relative overflow-hidden">
                <LazyImage 
                  src={img} 
                  alt="" 
                  className="w-full h-full object-cover"
                  wrapperClassName="w-full h-full"
                  showSpinner={false}
                />
              </div>
            ))}
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A] via-[#0A0A0A]/90 to-[#0A0A0A]" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block bg-[#B87333]/20 text-[#B87333] font-semibold text-sm uppercase tracking-widest px-5 py-2 rounded-full mb-6">
            {t("projectsPage.badge")}
          </span>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-['Cormorant_Garamond'] font-bold text-white mb-6">
            {t("projectsPage.title1")} <span className="text-copper-gradient">{t("projectsPage.title2")}</span>
          </h1>
          <p className="text-xl text-white/60 max-w-3xl mx-auto leading-relaxed">
            {t("projectsPage.description")}
          </p>
        </div>
      </section>

      {/* Stats Bar - Copper Gradient */}
      <section className="bg-gradient-to-r from-[#B87333] to-[#CD7F32] py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <AnimatedCounter end={500} suffix="+" label={t("stats.projects")} />
            <AnimatedCounter end={100} suffix="%" label={t("stats.satisfaction")} />
            <AnimatedCounter end={15} suffix="+" label={t("stats.experience")} />
            <AnimatedCounter end={3} label={t("stats.regions")} />
          </div>
        </div>
      </section>

      {/* Filter - Light */}
      <section className="py-6 bg-white border-b border-gray-100 sticky top-20 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center space-x-4">
              <Filter size={20} className="text-[#B87333]" />
              <p className="text-[#6B6B6B]">
                <span className="font-semibold text-[#0A0A0A]">{filteredProjects.length}</span> {t("projectsPage.projects")}{" "}
                {filter !== "all" && <span>dans <span className="text-[#B87333] font-medium">"{filter}"</span></span>}
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger
                  data-testid="category-filter"
                  className="w-[220px] border-gray-200 rounded-full bg-white"
                >
                  <SelectValue placeholder={t("projectsPage.filter")} />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {cat === "all" ? t("projectsPage.all") : cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <div className="flex border border-gray-200 rounded-full overflow-hidden bg-white">
                <button
                  onClick={() => setViewMode("grid")}
                  className={`px-4 py-2 text-sm flex items-center gap-2 transition-all ${viewMode === "grid" ? "bg-[#B87333] text-white" : "text-[#6B6B6B] hover:bg-gray-50"}`}
                >
                  <Grid3X3 size={16} />
                  {t("projectsPage.grid")}
                </button>
                <button
                  onClick={() => setViewMode("slider")}
                  className={`px-4 py-2 text-sm flex items-center gap-2 transition-all ${viewMode === "slider" ? "bg-[#B87333] text-white" : "text-[#6B6B6B] hover:bg-gray-50"}`}
                >
                  <SlidersHorizontal size={16} />
                  {t("projectsPage.slider")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Projects Grid - Light Background */}
      <section className="py-16 bg-gradient-to-b from-[#FAF9F7] to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {viewMode === "grid" ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProjects.map((project, index) => (
                <div
                  key={project.id}
                  data-testid={`project-card-${project.id}`}
                  className="bg-white rounded-3xl overflow-hidden shadow-lg card-modern group"
                >
                  <Link to={`/projets/${project.slug}`} className="block">
                    <div className="relative h-72 overflow-hidden cursor-pointer">
                      <LazyImage
                        src={project.after_image}
                        alt={project.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        wrapperClassName="w-full h-full"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      <div className="absolute bottom-0 left-0 right-0 p-6 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                        <span className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm text-white text-sm px-4 py-2 rounded-full">
                          <ArrowRight size={16} />
                          Voir le projet
                        </span>
                      </div>
                      <span className="absolute top-4 right-4 bg-gradient-to-r from-[#B87333] to-[#CD7F32] text-white text-xs font-semibold px-4 py-2 rounded-full shadow-lg">
                        {project.category}
                      </span>
                    </div>
                  </Link>
                  <div className="p-6">
                    <Link to={`/projets/${project.slug}`}>
                      <h3 className="font-['Cormorant_Garamond'] text-xl font-semibold text-[#0A0A0A] mb-2 group-hover:text-[#B87333] transition-colors">
                        {project.title}
                      </h3>
                    </Link>
                    <p className="text-[#6B6B6B] text-sm mb-4 line-clamp-2">
                      {project.description}
                    </p>
                    <div className="flex items-center justify-between text-sm mb-4">
                      <span className="flex items-center text-[#9A9A9A]">
                        <MapPin size={14} className="mr-1 text-[#B87333]" />
                        {project.location}
                      </span>
                      <span className="flex items-center text-[#9A9A9A]">
                        <Calendar size={14} className="mr-1 text-[#B87333]" />
                        {project.year}
                      </span>
                    </div>
                    <Link to={`/projets/${project.slug}`}>
                      <Button 
                        variant="outline" 
                        className="w-full border-[#B87333] text-[#B87333] hover:bg-[#B87333] hover:text-white rounded-full"
                      >
                        Voir les détails
                        <ArrowRight className="ml-2" size={16} />
                      </Button>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-8">
              {filteredProjects.map((project) => (
                <div
                  key={project.id}
                  className="bg-white rounded-3xl overflow-hidden shadow-lg p-6 card-modern"
                >
                  <div className="before-after-container">
                    <BeforeAfterSlider
                      beforeImage={project.before_image}
                      afterImage={project.after_image}
                      beforeLabel={t("beforeAfter.before")}
                      afterLabel={t("beforeAfter.after")}
                    />
                  </div>
                  <div className="mt-6 text-center">
                    <span className="inline-block bg-[#B87333]/10 text-[#B87333] text-xs font-semibold px-3 py-1 rounded-full mb-2">
                      {project.category}
                    </span>
                    <Link to={`/projets/${project.slug}`}>
                      <h3 className="font-['Cormorant_Garamond'] text-xl font-semibold text-[#0A0A0A] hover:text-[#B87333] transition-colors">
                        {project.title}
                      </h3>
                    </Link>
                    <p className="text-[#6B6B6B] text-sm mt-2 mb-4">{project.location} • {project.year}</p>
                    <Link to={`/projets/${project.slug}`}>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="border-[#B87333] text-[#B87333] hover:bg-[#B87333] hover:text-white rounded-full"
                      >
                        Voir les détails
                        <ArrowRight className="ml-2" size={14} />
                      </Button>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Featured Transformation - White */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block bg-[#B87333]/10 text-[#B87333] font-semibold text-sm uppercase tracking-widest px-5 py-2 rounded-full mb-6">
              {t("projectsPage.featured.badge")}
            </span>
            <h2 className="text-4xl sm:text-5xl font-['Cormorant_Garamond'] font-bold text-[#0A0A0A]">
              {t("projectsPage.featured.title1")} <span className="text-copper-gradient">{t("projectsPage.featured.title2")}</span>
            </h2>
          </div>
          
          {projects.length > 0 && (
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <h3 className="text-3xl font-['Cormorant_Garamond'] font-bold text-[#0A0A0A] mb-4">
                  {projects[0]?.title}
                </h3>
                <p className="text-lg text-[#6B6B6B] mb-6 leading-relaxed">
                  {projects[0]?.description}
                </p>
                <div className="grid grid-cols-2 gap-4 mb-8">
                  <div className="bg-[#FAF9F7] p-5 rounded-xl border border-gray-100">
                    <div className="text-[#B87333] font-semibold text-sm uppercase tracking-wider">{t("common.location")}</div>
                    <div className="text-[#0A0A0A] mt-1 font-medium">{projects[0]?.location}</div>
                  </div>
                  <div className="bg-[#FAF9F7] p-5 rounded-xl border border-gray-100">
                    <div className="text-[#B87333] font-semibold text-sm uppercase tracking-wider">{t("common.category")}</div>
                    <div className="text-[#0A0A0A] mt-1 font-medium">{projects[0]?.category}</div>
                  </div>
                </div>
                <Link to="/devis">
                  <Button className="bg-[#B87333] hover:bg-[#9A5F2A] text-white rounded-full px-8 py-6 shadow-lg shadow-[#B87333]/20">
                    {t("common.requestQuote")}
                    <ArrowRight className="ml-2" size={18} />
                  </Button>
                </Link>
              </div>
              <div className="order-1 lg:order-2">
                <div className="before-after-container">
                  <BeforeAfterSlider
                    beforeImage={projects[0]?.before_image}
                    afterImage={projects[0]?.after_image}
                    beforeLabel={t("beforeAfter.before")}
                    afterLabel={t("beforeAfter.after")}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Selected Project Modal */}
      {selectedProject && (
        <div
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
          onClick={() => setSelectedProject(null)}
        >
          <div
            className="bg-white rounded-3xl max-w-5xl w-full max-h-[90vh] overflow-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-8">
              <div className="flex justify-between items-start mb-8">
                <div>
                  <span className="inline-block bg-[#B87333]/10 text-[#B87333] text-sm font-semibold px-3 py-1 rounded-full mb-2">
                    {selectedProject.category}
                  </span>
                  <h3 className="font-['Cormorant_Garamond'] text-3xl font-bold text-[#0A0A0A]">
                    {selectedProject.title}
                  </h3>
                  <p className="text-[#6B6B6B] mt-2">
                    {selectedProject.location} • {selectedProject.year}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedProject(null)}
                  className="p-3 hover:bg-gray-100 rounded-full transition-colors"
                  data-testid="close-modal-btn"
                >
                  <X size={24} className="text-[#6B6B6B]" />
                </button>
              </div>
              
              <div className="before-after-container">
                <BeforeAfterSlider
                  beforeImage={selectedProject.before_image}
                  afterImage={selectedProject.after_image}
                  beforeLabel={t("beforeAfter.before")}
                  afterLabel={t("beforeAfter.after")}
                />
              </div>
              
              <p className="mt-8 text-lg text-[#6B6B6B] leading-relaxed">
                {selectedProject.description}
              </p>
              
              <div className="mt-8 flex justify-center">
                <Link to="/devis" onClick={() => setSelectedProject(null)}>
                  <Button className="bg-[#B87333] hover:bg-[#9A5F2A] text-white rounded-full px-8 py-6 shadow-lg">
                    {t("common.requestQuote")}
                    <ArrowRight className="ml-2" size={18} />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CTA - Dark */}
      <section className="py-24 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl font-['Cormorant_Garamond'] font-bold text-white mb-6">
            {t("projectsPage.ctaTitle1")} <span className="text-copper-gradient">{t("projectsPage.ctaTitle2")}</span>
          </h2>
          <p className="text-xl text-white/60 mb-10 max-w-2xl mx-auto">
            {t("projectsPage.ctaDescription")}
          </p>
          <Link to="/devis">
            <Button
              data-testid="projects-cta-quote"
              className="bg-gradient-to-r from-[#B87333] to-[#CD7F32] hover:from-[#CD8B4A] hover:to-[#D4AF37] text-white px-10 py-8 text-lg rounded-full shadow-xl shadow-[#B87333]/30 cta-pulse"
            >
              {t("common.freeQuote")}
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>

      {/* Lightbox */}
      <ImageLightbox
        images={filteredProjects.map(p => ({
          url: p.after_image,
          title: p.title,
          description: `${p.location} • ${p.category}`
        }))}
        initialIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />
    </div>
  );
};

export default ProjectsPage;
